import dayjs from "dayjs";

import { RUNNER_JOB_STATE_UPDATE_EVENT, type WSNotificationMessage } from "../../../../../../app/hooks";
import type { FileMetaDto, JobStatusDto, ModelStatusDto } from "../../../api";
import type { FileSystemTab, SortType } from "../../../store";
import { DocumentsTypes } from "../../../types";
import {
  drawingToFileMeta,
  filterDrawings,
  type DrawingCollectionRow,
  type DrawingKind,
  type DrawingPlane,
} from "../../drawings-collection/drawings-collection.lib";
import type { ContextMenuStateFunctions } from "../hooks";

// ---------------------------------- TYPES ----------------------------------

export type FileMetaWithExtras = FileMetaDto & {
  progress?: number;
  markupsCount?: number;
};

export type TableRow = {
  id: string;
  fileName: FileMetaWithExtras;
  version: string;
  author: string;
  date: string;
  time: string;
  file: FileMetaWithExtras;
  size: string;
  kind?: DrawingKind;
  ifcBinding?: string[];
  plane?: DrawingPlane;
  expert?: string;
};

export type TableViewType = {
  onClickModel: (file: FileMetaDto) => void;
  stateFunctions: ContextMenuStateFunctions;
  setSelectedFile: (file: FileMetaDto) => void;
  disabledRight: boolean;
  drawingsSearch?: string;
  drawings?: DrawingCollectionRow[];
  drawingsLoading?: boolean;
  /** Isolated IFC list for bind-to-model modal (does not touch fileStore / checkedFiles). */
  variant?: "default" | "models-picker" | "drawings-collection";
  pickerFiles?: FileMetaDto[];
  pickerSearch?: string;
  pickerIsLoading?: boolean;
  pickerSelectedIds?: string[];
  onPickerSelectionChange?: (files: FileMetaDto[]) => void;
};

export type ModelStatusKind = "error" | "pending";

const MODEL_STATUS_OUTPUT_FORMATS = new Set<string>(["GEOMETRY", "PROPERTIES"]);

export const isModelStatusOutputFormat = (outputFormat: string | null | undefined): boolean =>
  typeof outputFormat === "string" && MODEL_STATUS_OUTPUT_FORMATS.has(outputFormat);

export type TableViewFlags = {
  isModelsPicker: boolean;
  isDrawingsTab: boolean;
  isDocsTab: boolean;
  isModelsTab: boolean;
};

type BuildTableRowsParams = {
  isDrawingsTab: boolean;
  drawings: DrawingCollectionRow[];
  drawingsSearch: string;
  sortType: SortType;
  sourceFiles: FileMetaDto[];
  markupsByFileId: Record<string, number>;
  userNamesById: Record<string, string | null>;
  ifcNamesById: Record<string, string | null>;
};

// ---------------------------------- CONSTANTS ----------------------------------

export const ITEM_CLASS_NAME =
  "min-w-72 p-3 paragraph-base text-black flex gap-4 items-center outline-none hover:bg-grey-300 hover:border-opacity-50";

// ---------------------------------- UTILS ----------------------------------

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 Б";
  }

  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;

  if (bytes < mb) {
    return `${(bytes / kb).toFixed(1)} Кб`;
  }

  if (bytes < gb) {
    return `${(bytes / mb).toFixed(1)} Мб`;
  }

  return `${(bytes / gb).toFixed(2)} Гб`;
};

export const getTableViewFlags = (variant: TableViewType["variant"] = "default", activeTab: FileSystemTab): TableViewFlags => {
  const isModelsPicker = variant === "models-picker";
  const isDrawingsCollection = variant === "drawings-collection";
  const isDrawingsTab = isDrawingsCollection || (!isModelsPicker && activeTab === "drawings");
  const isDocsTab = !isModelsPicker && !isDrawingsCollection && activeTab === "docs";
  const isModelsTab = variant === "default" && activeTab === "models";

  return { isModelsPicker, isDrawingsTab, isDocsTab, isModelsTab };
};

export const getSourceFiles = (visibleFiles: FileMetaDto[], isModelsPicker: boolean, pickerSearch: string): FileMetaDto[] => {
  if (!isModelsPicker) {
    return visibleFiles;
  }

  const query = pickerSearch.trim().toLowerCase();

  if (!query) {
    return visibleFiles;
  }

  return visibleFiles.filter((file) => file.baseName.toLowerCase().includes(query));
};

type GetAuthorIdsParams = {
  isDrawingsTab: boolean;
  isDocsTab: boolean;
  drawings?: DrawingCollectionRow[];
  sourceFiles: FileMetaDto[];
};

export const getAuthorIds = ({ isDrawingsTab, isDocsTab, drawings, sourceFiles }: GetAuthorIdsParams): string[] => {
  if (isDrawingsTab) {
    return (drawings ?? []).map((drawing) => drawing.expert);
  }

  if (isDocsTab) {
    return sourceFiles.map((file) => file.author);
  }

  return [];
};

export const getIfcIds = (drawings: DrawingCollectionRow[] | undefined, isDrawingsTab: boolean): string[] => {
  if (!isDrawingsTab) {
    return [];
  }

  return (drawings ?? []).flatMap((drawing) => drawing.ifcBinding);
};

export const getTableId = ({
  isModelsPicker,
  isDrawingsTab,
  isDocsTab,
}: Pick<TableViewFlags, "isModelsPicker" | "isDrawingsTab" | "isDocsTab">): string => {
  if (isModelsPicker) {
    return "models-picker-table";
  }

  if (isDrawingsTab) {
    return "drawings-collection-table";
  }

  return isDocsTab ? "filesystem-docs-table" : "filesystem-table";
};

export const getFilesSummary = (files: readonly FileMetaDto[]): { count: number; size: number } => ({
  count: files.length,
  size: files.reduce((sum, file) => sum + (file.size || 0), 0),
});

export const getModelIdsForMarkups = (sourceFiles: FileMetaDto[], isDrawingsTab: boolean, isDocsTab: boolean): string[] => {
  if (isDrawingsTab || isDocsTab) {
    return [];
  }

  return sourceFiles.filter((file) => file.type !== DocumentsTypes.pdf_document).map((file) => file.id);
};

export const getModelIdsForStatus = (sourceFiles: FileMetaDto[], isModelsTab: boolean): string[] => {
  if (!isModelsTab) {
    return [];
  }

  return sourceFiles.map((file) => file.id);
};

export const buildMarkupsByFileId = (
  modelIds: readonly string[],
  markupsCounts: ReadonlyArray<number | null | undefined>,
): Record<string, number> => {
  const result: Record<string, number> = {};

  modelIds.forEach((id, index) => {
    result[id] = markupsCounts[index] ?? 0;
  });

  return result;
};

const ERROR_JOB_STATES = new Set(["failed", "error"]);
const PENDING_JOB_STATES = new Set(["waiting", "running", "working"]);

export const getModelStatusKindByState = (state: string | null | undefined): ModelStatusKind | undefined => {
  const normalized = state?.trim().toLowerCase();

  if (!normalized) {
    return undefined;
  }

  if (ERROR_JOB_STATES.has(normalized)) {
    return "error";
  }

  if (PENDING_JOB_STATES.has(normalized)) {
    return "pending";
  }

  return undefined;
};

export const getModelStatusKind = (jobs: JobStatusDto[] | null | undefined): ModelStatusKind | undefined => {
  let isPending = false;

  for (const job of jobs ?? []) {
    if (!isModelStatusOutputFormat(job.outputFormat)) {
      continue;
    }

    const kind = getModelStatusKindByState(job.state);

    if (kind === "error") {
      return "error";
    }

    if (kind === "pending") {
      isPending = true;
    }
  }

  return isPending ? "pending" : undefined;
};

export const buildModelStatusesById = (statuses: ModelStatusDto[]): Record<string, ModelStatusKind> => {
  const result: Record<string, ModelStatusKind> = {};

  for (const status of statuses) {
    if (!status.modelId) {
      continue;
    }

    const kind = getModelStatusKind(status.jobs);

    if (kind) {
      result[status.modelId] = kind;
    }
  }

  return result;
};

export const MODELS_STATUS_QUERY_KEY = (projectId: string | null | undefined, modelIds: string[]) => ["models-status", projectId, modelIds] as const;

export const mergeModelStatusKinds = (
  base: Record<string, ModelStatusKind>,
  overrides: Record<string, ModelStatusKind | null>,
): Record<string, ModelStatusKind> => {
  const result = { ...base };

  for (const [modelId, kind] of Object.entries(overrides)) {
    if (kind) {
      result[modelId] = kind;
    } else {
      delete result[modelId];
    }
  }

  return result;
};

const sortDrawings = (drawings: DrawingCollectionRow[], sortType: SortType, userNamesById: Record<string, string | null>) =>
  [...drawings].toSorted((a, b) => {
    if (sortType === "file.DESC") {
      return b.fileName.localeCompare(a.fileName, "ru");
    }

    if (sortType === "author.ASC") {
      const aName = userNamesById[a.expert] ?? a.expert;
      const bName = userNamesById[b.expert] ?? b.expert;

      return (aName ?? "").localeCompare(bName ?? "", "ru");
    }

    if (sortType === "author.DESC") {
      const aName = userNamesById[a.expert] ?? a.expert;
      const bName = userNamesById[b.expert] ?? b.expert;

      return (bName ?? "").localeCompare(aName ?? "", "ru");
    }

    if (sortType === "date.ASC" || sortType === "date.DESC") {
      return sortType === "date.ASC" ? a.createdAtMs - b.createdAtMs : b.createdAtMs - a.createdAtMs;
    }

    return a.fileName.localeCompare(b.fileName, "ru");
  });

const buildDrawingRows = (drawings: DrawingCollectionRow[]): TableRow[] =>
  drawings.map((drawing) => {
    const file = drawingToFileMeta(drawing);

    return {
      id: drawing.id,
      fileName: file,
      version: "",
      author: drawing.expert,
      date: drawing.date,
      time: drawing.time,
      file,
      size: "",
      kind: drawing.kind,
      ifcBinding: drawing.ifcBinding,
      plane: drawing.plane,
      expert: drawing.expert,
    } satisfies TableRow;
  });

const buildFileRows = (sourceFiles: FileMetaDto[], markupsByFileId: Record<string, number>): TableRow[] =>
  sourceFiles.map((file) => {
    const markupsCount = markupsByFileId[file.id] ?? 0;
    const fileWithMarkups: FileMetaWithExtras = { ...file, markupsCount };

    return {
      id: file.id,
      fileName: fileWithMarkups,
      version: `Версия ${file.version}`,
      author: file.author,
      date: dayjs.unix(Number(file.modifiedAt)).format("DD.MM.YYYY"),
      time: dayjs.unix(Number(file.modifiedAt)).format("HH:mm:ss"),
      file: fileWithMarkups,
      size: formatFileSize(file.size),
    } satisfies TableRow;
  });

export const buildTableRows = ({
  isDrawingsTab,
  drawings,
  drawingsSearch,
  sortType,
  sourceFiles,
  markupsByFileId,
  userNamesById,
  ifcNamesById,
}: BuildTableRowsParams): TableRow[] => {
  if (isDrawingsTab) {
    const filteredDrawings = filterDrawings(drawings, drawingsSearch, { userNamesById, ifcNamesById });
    const sortedDrawings = sortDrawings(filteredDrawings, sortType, userNamesById);

    return buildDrawingRows(sortedDrawings);
  }

  return buildFileRows(sourceFiles, markupsByFileId);
};

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

export const parseModelIds = (value?: string | null): string[] => {
  if (!value) {
    return [];
  }

  const parsed = parseJson(value);
  const unwrapped = typeof parsed === "string" ? parseJson(parsed) : parsed;

  if (Array.isArray(unwrapped)) {
    return unwrapped.filter((id): id is string => typeof id === "string");
  }

  const cleaned = value.replace(/[\\"[\]]/g, "").trim();

  return cleaned
    ? cleaned
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
    : [];
};

type RunnerJobStateUpdate = {
  modelIds: string[];
  statusKind: ModelStatusKind | null;
};

export const parseRunnerJobStateMessage = (rawData: string, knownModelIds: readonly string[]): RunnerJobStateUpdate | null => {
  let message: WSNotificationMessage;

  try {
    message = JSON.parse(rawData) as WSNotificationMessage;
  } catch {
    return null;
  }

  if (message.name !== RUNNER_JOB_STATE_UPDATE_EVENT) {
    return null;
  }

  const state = message.params?.state;

  if (!state || !isModelStatusOutputFormat(message.params?.outputFormat)) {
    return null;
  }

  const modelIds = parseModelIds(message.params?.modelId).filter((modelId) => knownModelIds.includes(modelId));

  if (modelIds.length === 0) {
    return null;
  }

  return { modelIds, statusKind: getModelStatusKindByState(state) ?? null };
};
